import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { toast } from "react-toastify";
import ReactModal from "react-modal";
import { useAuth0 } from "@auth0/auth0-react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import ai from "../../helpers/axios";
import TextInput from "../text-input";
import Loader from "../loader";
import { LoadingButton } from "../shared/loading-button";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "fieldChanged": {
      if (action.field === "selectedItem.name") {
        return {
          ...state,
          selectedItem: {
            ...(state.selectedItem || {}),
            name: action.value,
          },
        };
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  data: [],
  totalLength: 0,
  isSaving: false,
  isAddNewItemMode: false,
  newTagName: "",
  selectedItem: null,
  isDeleteModalOpen: false,
};

const CateringTagsList = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = useCallback(
    async () => {
      dispatch({ type: "dataBeganDownloading" });

      const token = await getAccessTokenSilently().catch(loginWithRedirect);

      const { data } = await ai.auth(token).get(`/api/catering/tags`, {});

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  useEffect(() => {
    getData();
  }, [getData]);

  const handleAddNewRow = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: true,
    });
  }, []);

  const handleHideNewRow = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "isAddNewItemMode",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "newTagName",
      value: "",
    });
  }, []);

  const handleAddNewItem = useCallback(async () => {
    if (!state.newTagName) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai.auth(token).post(`/api/catering/tags`, {
        name: state.newTagName,
      });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isAddNewItemMode",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "newTagName",
        value: "",
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.newTagName, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleMoveItemUp = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/tags/${item._id}`, { name: item.name, order: item.order + 1 });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleMoveItemDown = useCallback(async (item) => {
    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/tags/${item._id}`, { name: item.name, order: item.order - 1 });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, getData]);

  const handleEditItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
  }, []);

  const handleEditItemConfirm = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    if (!state.selectedItem.name) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .put(`/api/catering/tags/${state.selectedItem._id}`, { name: state.selectedItem.name });
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedItem, getAccessTokenSilently, loginWithRedirect, getData]);

  const handleCancelEditItem = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: null,
    });
  }, []);

  const handleDeleteItem = useCallback((item) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: true,
    });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: null,
    });
    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: false,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);
      await ai
        .auth(token)
        .delete(`/api/catering/tags/${state.selectedItem._id}`);
      await getData();

      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "isDeleteModalOpen",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedItem",
        value: null,
      });
    } catch (error) {
      dispatch({
        type: "fieldChanged",
        field: "isSaving",
        value: false,
      });
    }
  }, [state.selectedItem, getAccessTokenSilently, loginWithRedirect, getData]);

  return (
    <Fragment>
      <h5 className="mt-30 mb-1 d-flex">
        Dietary Tags

        <button
          className="base-button icon-button icon-button-small evo-dark-green ml-2"
          title="Add Dietary Tag"
          onClick={handleAddNewRow}
          disabled={state.isAddNewItemMode || !!state.selectedItem || state.isSaving}
        >
          <span className="material-icons">add_circle</span>
        </button>
      </h5>

      <div className="position-relative">
        {state.isLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>}

        <TableContainer
          component={Paper}
          className="mt-0 shadow-none"
        >
          <Table>
            <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
              <TableRow className="fm-l2-parent-table-head-row">
                <TableCell>
                  Tag Names
                </TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>

            <TableBody className="fm-l2-parent-table-body">
              {state.isAddNewItemMode && (
                <TableRow className="fm-l2-parent-table-row">
                  <TableCell>
                    <TextInput
                      name="newTagName"
                      handleChange={(event) => {
                        dispatch({
                          type: "fieldChanged",
                          field: "newTagName",
                          value: event.target.value,
                        });
                      }}
                      value={state.newTagName}
                      className="w-100"
                    />
                  </TableCell>
                  <TableCell className="fm-l2-parent-table-row-actions">
                    <div className="d-flex justify-content-end">
                      <button
                        className="d-block base-button icon-button icon-button-small btn-green-inverted"
                        onClick={handleAddNewItem}
                        disabled={!state.newTagName || state.isSaving}
                        title="Save"
                      >
                        <span className="material-icons">check_circle</span>
                      </button>

                      <button
                        className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                        onClick={handleHideNewRow}
                        disabled={state.isSaving}
                        title="Cancel"
                      >
                        <span className="material-icons">cancel</span>
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {state.data.length > 0 && state.data.map((row, index) => (
                <TableRow
                  className="fm-l2-parent-table-row"
                  key={row._id}
                >
                  <TableCell>
                    {!state.isDeleteModalOpen && state.selectedItem?._id === row._id ? (
                      <TextInput
                        name="selectedItemName"
                        handleChange={(event) => {
                          dispatch({
                            type: "fieldChanged",
                            field: "selectedItem.name",
                            value: event.target.value,
                          });
                        }}
                        value={state.selectedItem?.name || ""}
                        className="w-100"
                      />
                    ) : (
                      row.name
                    )}
                  </TableCell>

                  <TableCell className="fm-l2-parent-table-row-actions">
                    {!state.isAddNewItemMode && !state.selectedItem && (
                      <div className="justify-content-end">
                        {index !== 0 && (
                          <button
                            className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                            onClick={() => handleMoveItemUp(row)}
                            disabled={state.isSaving}
                            title="Move Dietary Tag Up"
                          >
                            <span className="material-icons">arrow_upward</span>
                          </button>
                        )}

                        {index !== state.data.length - 1 && (
                          <button
                            className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                            onClick={() => handleMoveItemDown(row)}
                            disabled={state.isSaving}
                            title="Move Dietary Tag Down"
                          >
                            <span className="material-icons">arrow_downward</span>
                          </button>
                        )}

                        <button
                          className="d-block base-button icon-button icon-button-small btn-blue-inverted"
                          onClick={() => handleEditItem(row)}
                          disabled={state.isSaving}
                          title="Edit Dietary Tag Name"
                        >
                          <span className="material-icons">edit</span>
                        </button>

                        <button
                          className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                          onClick={() => handleDeleteItem(row)}
                          disabled={state.isSaving}
                          title="Delete Dietary Tag"
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    )}
                    {!state.isDeleteModalOpen && state.selectedItem?._id === row._id && (
                      <div className="d-flex justify-content-end">
                        <button
                          className="d-block base-button icon-button icon-button-small btn-green-inverted"
                          onClick={handleEditItemConfirm}
                          disabled={!state.selectedItem?.name || state.isSaving}
                          title="Save"
                        >
                          <span className="material-icons">check_circle</span>
                        </button>

                        <button
                          className="d-block base-button icon-button icon-button-small btn-danger-inverted"
                          onClick={handleCancelEditItem}
                          disabled={state.isSaving}
                          title="Cancel"
                        >
                          <span className="material-icons">cancel</span>
                        </button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!state.data.length && !state.isAddNewItemMode && (
                <TableRow
                  className="fm-l2-parent-table-row"
                >
                  <TableCell colSpan={2}>
                    There are no records to display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <ReactModal
        isOpen={state.isDeleteModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseDeleteModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Dietary Tag</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Are you sure you want to delete this dietary tag? This action cannot be undone.
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseDeleteModal}
              disabled={state.isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteConfirm}
              isLoading={state.isSaving}
            >
              Delete Dietary Tag
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default CateringTagsList;
